import React from "react";
import { Box, Typography, makeStyles, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import AppDialog from "components/Dialog/AppDialog";
import AppColor from "utilities/AppColor";

const useStyles = makeStyles(() => ({
  tagRequired: {
    display: "inline-block",
    padding: "2px 6px",
    fontSize: 11,
    fontWeight: 600,
    backgroundColor: "#FEE2E2",
    color: "#991B1B",
    borderRadius: "4px",
    marginLeft: "8px",
  },
  tagOptional: {
    display: "inline-block",
    padding: "2px 6px",
    fontSize: 11,
    fontWeight: 600,
    backgroundColor: "#E5E7EB",
    color: "#374151",
    borderRadius: "4px",
    marginLeft: "8px",
  },
  bulletPoint: {
    position: "relative",
    paddingLeft: "12px",
    "&::before": {
      content: '"•"',
      position: "absolute",
      left: 0,
      color: "#9CA3AF",
    },
  },
  tableContainer: {
    maxHeight: 500,
    marginTop: 16,
    border: "1px solid #E5E7EB",
    borderRadius: 8,
  },
  tableCellHead: {
    fontSize: 13,
    fontWeight: 700,
    color: "#374151",
    textTransform: "uppercase",
    borderBottom: "1px solid #E5E7EB",
    padding: "12px 16px",
    backgroundColor: "#F9FAFB",
  },
  tableCellBody: {
    fontSize: 13,
    color: "#4B5563",
    borderBottom: "1px solid #E5E7EB",
    padding: "12px 16px",
    verticalAlign: "top",
  },
  groupRow: {
    backgroundColor: "#F3F4F6",
  },
  groupText: {
    fontWeight: 700,
    fontSize: 14,
    color: AppColor.mainColor,
  }
}));

const getRequiredFields = (t) => [
  { name: "label.first_name", details: [t('importRules.not_empty'), t('importRules.max_length', {value: 64})] },
  { name: "label.last_name", details: [t('importRules.not_empty'), t('importRules.max_length', {value: 64})] },
  { name: "label.email", details: [t('importRules.not_empty'), t('importRules.email_format'), t('importRules.max_length', {value: 255}), t('importRules.unique_email')] },
  { name: "label.recipientEmail", details: [t('importRules.not_empty'), t('importRules.email_format'), t('importRules.max_length', {value: 255}), t('importRules.unique_csv')] },
  { name: "label.recipient_type", details: [
    <div>
      {t("importRules.values")}:
      <div style={{ marginLeft: 12 }}>
        <div>SUPPORT_COORDINATOR</div>
        <div>FAMILY_MEMBER_OR_GUARDIAN</div>
        <div>NOMINEE</div>
        <div>PARTICIPANT</div>
      </div>
    </div>
  ] },
];

const getPersonalInfoFields = (t) => [
  { name: "label.phone", details: [t('importRules.only_number')] },
  { name: "label.dateOfBirth", details: [t('importRules.max_length', {value: 64}), t('importRules.format', { value: 'yyyy/MM/dd'})] },
  { name: "label.ndisPlanNumber", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.address", details: [t('importRules.max_length_no_limit')] },
  { name: "label.postalCode", details: [t('importRules.max_length', {value: 64})] },
  { name: "label.startDate", details: [t('importRules.max_length', {value: 64}), t('importRules.format', { value: 'yyyy/MM/dd'})] },
  { name: "label.endDate", details: [t('importRules.max_length', {value: 64}), t('importRules.format', { value: 'yyyy/MM/dd'})] },
  { name: "label.serviceType", details: [t('importRules.enum_service_type')] },
  { name: "label.silLevel", details: [t('importRules.enum_sil_level')] },
];

const getEmergencyFields = (t) => [
  { name: "label.firstName", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.lastName", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.relationship", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.phone", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.email", details: [t('importRules.max_length', {value: 255}), t('importRules.email_format')] },
];

const getSupportCoordinatorFields = (t) => [
  { name: "label.firstName", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.lastName", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.organizationName", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.phone", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.email", details: [t('importRules.max_length', {value: 255}), t('importRules.email_format')] },
];

const getGPFields = (t) => [
  { name: "label.firstName", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.lastName", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.practice_name", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.phone", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.address", details: [t('importRules.max_length', {value: 255})] },
];

const getPlanManagerFields = (t) => [
  { name: "label.organizationName", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.contact_name", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.phone", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.email", details: [t('importRules.max_length', {value: 255}), t('importRules.email_format')] },
];

const getBillingFields = (t) => [
  { name: "label.billing_type", details: [t('importRules.enum_billing_type')] },
  { name: "label.organizationName", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.contact_name", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.address", details: [t('importRules.max_length', {value: 500})] },
  { name: "label.email", details: [t('importRules.max_length', {value: 255}), t('importRules.email_format')] },
  { name: "label.phone", details: [t('importRules.max_length', {value: 255})] },
];

const getCarePlanFields = (t) => [
  { name: "label.primary_diagnosis", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.secondary_conditions", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.personal_care", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.mobility", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.medication_management", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.community_access", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.communication_strategy", details: [t('importRules.max_length', {value: 255})] },
  { name: "label.what_matters", details: [t('importRules.max_length', {value: 10000})] },
];

const ImportClientRulesModal = ({ open, onClose }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const groups = [
    { 
      title: "1. Personal Info & Plan", 
      fields: [
        ...getRequiredFields(t).map(f => ({ ...f, required: true })),
        ...getPersonalInfoFields(t).map(f => ({ ...f, required: false }))
      ] 
    },
    { title: "2. Emergency Contact", fields: getEmergencyFields(t).map(f => ({ ...f, required: false })) },
    { title: "3. Support Coordinator", fields: getSupportCoordinatorFields(t).map(f => ({ ...f, required: false })) },
    { title: "4. GP / Doctor", fields: getGPFields(t).map(f => ({ ...f, required: false })) },
    { title: "5. Plan Manager", fields: getPlanManagerFields(t).map(f => ({ ...f, required: false })) },
    { title: "6. Billing", fields: getBillingFields(t).map(f => ({ ...f, required: false })) },
    { title: "7. Care Plan", fields: getCarePlanFields(t).map(f => ({ ...f, required: false })) }
  ];

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={t("importRules.particularRulesTitle")}
      maxWidth="md"
    >
      <Box p={2}>
        <Typography variant="body2" style={{ marginBottom: 16, color: "#4B5563" }}>
          {t("importRules.description")}
        </Typography>

        <TableContainer component={Paper} elevation={0} className={classes.tableContainer}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableCellHead} style={{ width: "30%" }}>{t("importRules.fieldName")}</TableCell>
                <TableCell className={classes.tableCellHead} style={{ width: "15%" }}>{t("importRules.required")}</TableCell>
                <TableCell className={classes.tableCellHead} style={{ width: "55%" }}>{t("importRules.formatOrRule")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((group, gIdx) => (
                <React.Fragment key={gIdx}>
                  <TableRow className={classes.groupRow}>
                    <TableCell colSpan={3}>
                      <Typography className={classes.groupText}>{group.title}</Typography>
                    </TableCell>
                  </TableRow>
                  {group.fields.map((field, fIdx) => (
                    <TableRow key={`${gIdx}-${fIdx}`}>
                      <TableCell className={classes.tableCellBody}>
                        <Box fontWeight={600} color="#111827">
                          {t(field.name)}
                        </Box>
                      </TableCell>
                      <TableCell className={classes.tableCellBody}>
                        {field.required ? (
                          <span className={classes.tagRequired}>{t("importRules.required")}</span>
                        ) : (
                          <span className={classes.tagOptional}>{t("importRules.optional")}</span>
                        )}
                      </TableCell>
                      <TableCell className={classes.tableCellBody}>
                        {field.details && field.details.length > 0 ? (
                          field.details.map((detail, dIdx) => (
                            <div key={dIdx} className={classes.bulletPoint}>{detail}</div>
                          ))
                        ) : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </AppDialog>
  );
};

export default ImportClientRulesModal;
